import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ReactComponent as FrontArrow } from "assets/icons/front-arrow.svg";
import { ReactComponent as BackArrow } from "assets/icons/back-arrow.svg";
import FlexBox from "ui/FlexBox";
import { hasHorizontalScroll, hasReachedEnd } from "utils/dom";
import { spacing } from "globalStyles";
import MenuItem from "./MenuItem";
import { MenuDeviceProps } from ".";
import { MENU_NAV_HEIGHT } from "utils/constants";

const MENU_SELECTED_BORDER = 3;
const SCROLL_PARCEL = 100;

type ScrollSide = "left" | "right";

const MenuLargeDevices = ({ menus }: MenuDeviceProps) => {
  const [hasScroll, setHasScroll] = useState(false);
  const [isLeftScrollArrowEnabled, setIsLeftScrollArrowEnabled] = useState(false);
  const [isRightScrollArrowEnabled, setIsRightScrollArrowEnabled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const clickInterval = useRef<number>();

  const checkScroll = useCallback(() => {
    if (!menuRef.current) {
      return;
    }

    const hasScrollResult = hasHorizontalScroll(menuRef.current);

    setHasScroll(hasScrollResult);

    setIsRightScrollArrowEnabled(hasScrollResult && !hasReachedEnd(menuRef.current));
    setIsLeftScrollArrowEnabled(hasScrollResult && menuRef.current.scrollLeft > 0);
  }, []);

  useLayoutEffect(() => {
    window.addEventListener("resize", checkScroll);
    checkScroll();

    return () => window.removeEventListener("resize", checkScroll);
  }, [checkScroll]);

  const handleScroll = (scrollSide: ScrollSide) => {
    if (!menuRef.current) {
      return;
    }

    if (scrollSide === "left") {
      menuRef.current.scrollLeft -= SCROLL_PARCEL;
    } else if (scrollSide === "right") {
      menuRef.current.scrollLeft += SCROLL_PARCEL;
    }

    checkScroll();
  };

  const handleMouseDown = (scrollSide: ScrollSide) =>
    (clickInterval.current = window.setInterval(() => {
      handleScroll(scrollSide);
    }, 100));

  const handleMouseUp = () => clearInterval(clickInterval.current);

  const handleScrollLeftArrowClick = () => handleScroll("left");

  const handleScrollLeftArrowPress = () => handleMouseDown("left");

  const handleScrollRightArrowClick = () => handleScroll("right");

  const handleScrollRightArrowPress = () => handleMouseDown("right");

  return (
    <Container>
      {hasScroll && (
        <ArrowContainer
          onMouseUp={handleMouseUp}
          onMouseDown={handleScrollLeftArrowPress}
          onClick={handleScrollLeftArrowClick}
          disabled={!isLeftScrollArrowEnabled}
        >
          <BackArrow />
        </ArrowContainer>
      )}
      <TabsContainer ref={menuRef} onScroll={checkScroll}>
        <Tabs>
          {menus.map((menu) => (
            <Tab key={menu.label}>
              <MenuItem {...menu} />
            </Tab>
          ))}
        </Tabs>
      </TabsContainer>
      {hasScroll && (
        <ArrowContainer
          onMouseDown={handleScrollRightArrowPress}
          onMouseUp={handleMouseUp}
          onClick={handleScrollRightArrowClick}
          disabled={!isRightScrollArrowEnabled}
        >
          <FrontArrow />
        </ArrowContainer>
      )}
    </Container>
  );
};

export default MenuLargeDevices;

const Container = styled.div`
  display: flex;
  align-items: center;
  height: ${MENU_NAV_HEIGHT}px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.menu.background};
  box-shadow: 0px 3px 6px ${({ theme }) => theme.colors.grayScale.darkest}29;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
`;

const TabsContainer = styled.div`
  height: 100%;
  overflow: hidden;
  max-width: 1900px;
  margin: 0 auto;
`;

const Tabs = styled.nav`
  height: 100%;
  display: flex;
  justify-content: space-between;
`;

const Tab = styled(FlexBox)`
  flex: 1;
  align-items: center;
  min-width: 278px;
  height: 100%;
  box-sizing: border-box;
  padding: 0 15px;
  border-top: ${MENU_SELECTED_BORDER}px solid
    ${({ theme }) => theme.colors.menu.background};
  border-bottom: ${MENU_SELECTED_BORDER}px solid
    ${({ theme }) => theme.colors.menu.background};

  :hover {
    border-bottom: ${MENU_SELECTED_BORDER}px solid
      ${({ theme }) => theme.colors.menu.selectedBorder};
  }
`;

const ArrowContainer = styled.div<{ disabled: boolean }>`
  cursor: ${({ disabled }) => (disabled ? "auto" : "pointer")};
  padding: 0 ${spacing.medium}px;
  min-width: 15px;

  svg {
    fill: ${({ disabled, theme }) =>
      disabled ? theme.colors.menu.disabledArrow : theme.colors.menu.arrow};
  }
`;
